import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, Plane, Hourglass, Wallet, Briefcase, Clock, Target } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr, monthLabel, fmtDate } from "../lib/format";
import { PageHeader, StatCard, Panel, StatusBadge, EmptyState } from "../components/Common";
import { CheckInWidget } from "../components/CheckInWidget";

const COLORS = ["#E08A1E", "#0A0807", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const AdminView = ({ d, reload }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard label="Headcount" value={d.headcount} icon={Users} testid="stat-headcount" />
      <StatCard label="Present today" value={d.present_today} icon={UserCheck} testid="stat-present" hint={`${d.on_leave_today} on leave`} />
      <StatCard label="Awaiting HR approval" value={d.pending_leaves} icon={Hourglass} testid="stat-pending-leaves" hint={`${d.pending_manager_leaves} with managers`} />
      <StatCard label="Payroll cost" value={inr(d.payroll_cost)} icon={Wallet} testid="stat-payroll" hint={d.payroll_month ? monthLabel(d.payroll_month) : "No payroll run yet"} accent />
      <StatCard label="Open jobs" value={d.open_jobs} icon={Briefcase} testid="stat-open-jobs" />
      <StatCard label="Candidates in pipeline" value={d.active_candidates} icon={Plane} testid="stat-candidates" />
    </div>
    <div className="grid lg:grid-cols-3 gap-4 mt-4">
      <Panel title="Attendance · last 7 days" className="lg:col-span-2" testid="attendance-trend-chart">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.attendance_trend}>
              <XAxis dataKey="date" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={28} />
              <Tooltip cursor={{ fill: "#FDF6ED" }} />
              <Bar dataKey="present" fill="#E08A1E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <CheckInWidget onChange={reload} />
    </div>
    <div className="grid lg:grid-cols-3 gap-4 mt-4">
      <Panel title="Departments" testid="department-chart">
        {d.departments.length ? (
          <>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.departments} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                    {d.departments.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5 mt-2">
              {d.departments.map((x, i) => (
                <div key={x.name} className="flex justify-between text-sm">
                  <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{x.name}</span>
                  <span className="font-semibold">{x.value}</span>
                </div>
              ))}
            </div>
          </>
        ) : <EmptyState text="No employees yet" />}
      </Panel>
      <Panel title="Recent leave requests" className="lg:col-span-2" actions={<Link to="/leaves" className="text-sm text-[#E08A1E] font-semibold" data-testid="view-all-leaves-link">View all</Link>}>
        {d.recent_leaves.length ? (
          <div className="divide-y divide-[#E5E5EA]">
            {d.recent_leaves.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-3 gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{l.user_name}</div>
                  <div className="text-xs text-[#6E6E73]">{l.leave_type_name || l.leave_type} · {fmtDate(l.start_date)} → {fmtDate(l.end_date)} · {l.days}d</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))}
          </div>
        ) : <EmptyState text="No leave requests yet" />}
      </Panel>
    </div>
  </>
);

const EmployeeView = ({ d, reload }) => (
  <>
    <div className="grid lg:grid-cols-3 gap-4">
      <CheckInWidget onChange={reload} />
      <div className="lg:col-span-2 grid grid-cols-2 gap-4">
        <StatCard label="Days present (month)" value={d.days_present_month} icon={UserCheck} testid="stat-days-present" />
        <StatCard label="Hours logged (month)" value={d.hours_month} icon={Clock} testid="stat-hours" />
        <StatCard label="Pending leaves" value={d.pending_leaves} icon={Hourglass} testid="stat-my-pending-leaves" hint={d.team_pending ? `${d.team_pending} team request(s) to approve` : undefined} />
        <StatCard label="Reviews to complete" value={d.pending_reviews} icon={Target} testid="stat-pending-reviews" />
      </div>
    </div>
    <div className="grid lg:grid-cols-3 gap-4 mt-4">
      <Panel title="Leave balance" className="lg:col-span-2" testid="dashboard-leave-balance">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(d.balance).filter(([, v]) => !v.unlimited).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[#F7EEE2] p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#6E6E73]">{v.name}</div>
              <div className="text-2xl font-extrabold mt-1">{v.available}<span className="text-sm font-medium text-[#6E6E73]"> available</span></div>
            </div>
          ))}
        </div>
      </Panel>
      <StatCard label="Latest net pay" value={d.latest_payslip ? inr(d.latest_payslip.net_pay) : "—"} icon={Wallet} accent testid="stat-latest-pay"
        hint={d.latest_payslip ? monthLabel(d.latest_payslip.month) : "No payslip yet"} />
    </div>
  </>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={user.role === "admin" ? "HR Command Center" : "Your workspace"} title={`${greeting()}, ${user.name.split(" ")[0]}`}
        subtitle={new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
      {!d ? <div className="text-sm text-[#6E6E73]">Loading...</div> : d.role === "admin" ? <AdminView d={d} reload={load} /> : <EmployeeView d={d} reload={load} />}
    </div>
  );
}
